import { Component, inject, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { DialogRef } from '@angular/cdk/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { AUTHORIZATION_TAA_ENDPOINT } from 'src/app/constants/api-routes';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

/** Data provided to the transaction request offer dialog: transaction request ID for TAA fetch */
export interface TransactionRequestOfferDialogData {
  /** Transaction request ID for the TAA to fetch */
  transactionRequestId: string;
  /** Optional buyer-selected plan so TAA reflects the chosen offer */
  planId?: number;
}

@Component({
  selector: 'app-transaction-request-offer-dialog',
  templateUrl: './transaction-request-offer-dialog.component.html',
  styleUrls: ['./transaction-request-offer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TransactionRequestOfferDialogComponent implements OnInit, OnDestroy {
  private _userService: UserService = inject(UserService);
  private _dialogRef: DialogRef<TransactionRequestOfferDialogComponent> = inject(DialogRef);
  private _http: HttpClient = inject(HttpClient);

  private _companySubscription: Subscription | null = null;

  public data: TransactionRequestOfferDialogData = inject(MAT_DIALOG_DATA);

  /** TAA HTML from backend, sanitized for binding */
  public taaHtml: string | null = null;

  /** Loading flag while fetching TAA */
  public isLoading = true;

  /** Error message from backend (JSON error body) or generic message */
  public errorMessage: string | null = null;

  ngOnInit(): void {
    this._companySubscription = this._userService.company$
      .pipe(take(1))
      .subscribe((company) => {
        if (company?.companyId != null) {
          this.fetchTaa(company.companyId);
        } else {
          this.isLoading = false;
          this.errorMessage = 'Company context is not available.';
        }
      });
  }

  ngOnDestroy(): void {
    this._companySubscription?.unsubscribe();
  }

  private fetchTaa(companyId: number): void {
    this.isLoading = true;
    this.errorMessage = null;
    const url = AUTHORIZATION_TAA_ENDPOINT(
      companyId,
      this.data.transactionRequestId,
      this.data.planId,
    );

    this._http.get(url, { responseType: 'text' }).subscribe({
      next: (html: string) => {
        // Rendered via [appSanitizedHtml], which sanitizes with DOMPurify
        this.taaHtml = html;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.taaHtml = null;

        const msg = err.error?.detail || 'Unable to load the Transaction Authorization Agreement.';
        this.errorMessage = msg;
      }
    });
  }

  public close(): void {
    this._dialogRef.close();
  }
}
